import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { FC, SyntheticEvent, useEffect, useState } from 'react';

import { clsx } from 'clsx';
import Image from 'next/image';

import { Modal } from '@shared/ui/Modal';

import cls from './LightBoxModal.module.scss';

type LightBoxModalProps = {
  open: boolean;
  imageUrls: string[];
  currentImageUrl: string;
  onClose: () => void;
};

export const LightBoxModal: FC<LightBoxModalProps> = ({ open, currentImageUrl, onClose, imageUrls }) => {
  const [currentImage, setCurrentImage] = useState(currentImageUrl);

  const isSeveralImages = imageUrls.length > 1;
  const isLocalImage = currentImage.startsWith('blob:') || currentImage.startsWith('data:');

  const handleChangeCurrentImage = (url: string) => (e: SyntheticEvent) => {
    e.stopPropagation();
    setCurrentImage(url);
  };

  const handleClickNextArrow = (e: SyntheticEvent) => {
    e.stopPropagation();

    const index = imageUrls.findIndex((item) => item === currentImage);

    if (index !== -1) {
      setCurrentImage(index < imageUrls.length - 1 ? imageUrls[index + 1] : imageUrls[0]);
    }
  };

  const handleClickPrevArrow = (e: SyntheticEvent) => {
    e.stopPropagation();

    const index = imageUrls.findIndex((item) => item === currentImage);

    if (index !== -1) {
      setCurrentImage(index > 0 ? imageUrls[index - 1] : imageUrls[imageUrls.length - 1]);
    }
  };

  useEffect(() => {
    if (open) {
      setCurrentImage(currentImageUrl);
    }
  }, [currentImageUrl, open]);

  return (
    <Modal
      className={cls.modal}
      contentClassName={cls.modalContent}
      withoutStopPropagation={true}
      isOpen={open}
      onClose={onClose}
    >
      <>
        <div
          className={cls.mainImageWrapper}
          onClick={onClose}
        >
          <Image
            className={cls.mainImage}
            src={currentImage}
            fill
            alt={'lightbox-modal-image'}
            unoptimized={isLocalImage}
          />
          {isSeveralImages && (
            <div className={cls.arrowsWrapper}>
              <button
                aria-label={'Предыдущее изображение'}
                className={cls.iconButtonStyled}
                data-testid={'lightbox-modal-prev-button'}
                onClick={handleClickPrevArrow}
              >
                <ChevronLeft className={cls.iconArrow} />
              </button>
              <button
                aria-label={'Следующее изображение'}
                className={cls.iconButtonStyled}
                data-testid={'lightbox-modal-next-button'}
                onClick={handleClickNextArrow}
                autoFocus
              >
                <ChevronRight className={cls.iconArrow} />
              </button>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          aria-label={'Закрыть галерею'}
          className={cls.iconButtonCloseStyled}
        >
          <Plus
            className={cls.closeIconStyles}
            onClick={onClose}
          />
        </button>

        {isSeveralImages && (
          <div className={cls.previewImageWrapper}>
            {imageUrls.map((url) => (
              <div
                className={clsx(cls.previewImage, url === currentImage ? cls.selected : undefined)}
                key={url}
                onClick={handleChangeCurrentImage(url)}
                style={{
                  backgroundImage: `url(${url})`
                }}
              />
            ))}
          </div>
        )}
      </>
    </Modal>
  );
};
